import { useState } from 'react';
import { Save, Plus, Trash2 } from 'lucide-react';
import DataTable from '@/components/DataTable';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
}

const MOCK_TEAM: TeamMember[] = [
  { id: '1', name: 'Sarah Chen', email: 'sarah@company.com', role: 'Admin', status: 'Active' },
  { id: '2', name: 'Mark Thompson', email: 'mark@company.com', role: 'Viewer', status: 'Active' },
  { id: '3', name: 'Priya Patel', email: 'priya@company.com', role: 'Editor', status: 'Active' },
  { id: '4', name: 'Alex Kim', email: 'alex@company.com', role: 'Viewer', status: 'Pending' },
];

const teamColumns = [
  { key: 'name', header: 'Name' },
  { key: 'email', header: 'Email' },
  {
    key: 'role',
    header: 'Role',
    render: (item: Record<string, unknown>) => (
      <span className="inline-flex rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
        {item.role as string}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (item: Record<string, unknown>) => (
      <span
        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
          item.status === 'Active'
            ? 'bg-green-100 text-green-700'
            : 'bg-yellow-100 text-yellow-700'
        }`}
      >
        {item.status as string}
      </span>
    ),
  },
  {
    key: 'actions',
    header: '',
    sortable: false,
    render: () => (
      <button className="text-gray-400 hover:text-red-500 transition-colors">
        <Trash2 className="h-4 w-4" />
      </button>
    ),
  },
];

export default function SettingsPage() {
  const [orgName, setOrgName] = useState('Acme Corporation');
  const [industry, setIndustry] = useState('Technology');
  const [companySize, setCompanySize] = useState('501-2000');
  const [saved, setSaved] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Viewer');
  const [team, setTeam] = useState<TeamMember[]>(MOCK_TEAM);

  const handleSaveOrg = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleInvite = () => {
    if (!inviteEmail) return;
    const newMember: TeamMember = {
      id: String(Date.now()),
      name: inviteEmail.split('@')[0],
      email: inviteEmail,
      role: inviteRole,
      status: 'Pending',
    };
    setTeam([...team, newMember]);
    setInviteEmail('');
    setShowInvite(false);
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your organization profile and team access
        </p>
      </div>

      {/* Organization Profile */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Organization Profile
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Name
            </label>
            <input
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Industry
            </label>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="input-field"
            >
              <option>Technology</option>
              <option>SaaS</option>
              <option>Financial Services</option>
              <option>Healthcare</option>
              <option>Retail</option>
              <option>Manufacturing</option>
              <option>Education</option>
              <option>Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Size
            </label>
            <select
              value={companySize}
              onChange={(e) => setCompanySize(e.target.value)}
              className="input-field"
            >
              <option>1-50</option>
              <option>51-100</option>
              <option>101-500</option>
              <option>501-2000</option>
              <option>2000+</option>
            </select>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button className="btn-primary" onClick={handleSaveOrg}>
            <Save className="mr-2 h-4 w-4" />
            Save Changes
          </button>
          {saved && (
            <span className="text-sm text-green-600 font-medium">
              Settings saved successfully
            </span>
          )}
        </div>
      </div>

      {/* User Management */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Team Members
          </h2>
          <button
            className="btn-primary"
            onClick={() => setShowInvite(!showInvite)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Invite Member
          </button>
        </div>

        {showInvite && (
          <div className="mb-4 rounded-lg border border-gray-200 p-4">
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="input-field flex-1"
                placeholder="colleague@company.com"
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="input-field w-full sm:w-36"
              >
                <option>Admin</option>
                <option>Editor</option>
                <option>Viewer</option>
              </select>
              <button className="btn-primary" onClick={handleInvite}>
                Send Invite
              </button>
            </div>
          </div>
        )}

        <DataTable
          columns={teamColumns}
          data={team as unknown as Record<string, unknown>[]}
          pageSize={10}
        />
      </div>

      {/* Account Settings */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Account Settings
        </h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
            <div>
              <p className="font-medium text-gray-900">Change Password</p>
              <p className="text-sm text-gray-500">
                Update your account password
              </p>
            </div>
            <button className="btn-secondary">Update</button>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
            <div>
              <p className="font-medium text-gray-900">Two-Factor Authentication</p>
              <p className="text-sm text-gray-500">
                Add an extra layer of security to your account
              </p>
            </div>
            <button className="btn-secondary">Enable</button>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-4">
            <div>
              <p className="font-medium text-red-900">Delete Account</p>
              <p className="text-sm text-red-600">
                Permanently delete your organization and all data
              </p>
            </div>
            <button className="rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
